import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, InputAdornment, IconButton,
  CircularProgress, Alert,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { auth } from 'services';

// Same strength rule as sign-up: 12+ chars, upper, lower, number, special.
const isStrong = (p) =>
  p.length >= 12 &&
  /[a-z]/.test(p) &&
  /[A-Z]/.test(p) &&
  /[0-9]/.test(p) &&
  /[^a-zA-Z0-9]/.test(p);

export const ChangePasswordSection = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    setError('');
    setSuccess(false);

    if (!current || !next || !confirm) {
      setError('Please fill in all three fields.');
      return;
    }
    if (!isStrong(next)) {
      setError('New password must be at least 12 characters and include an uppercase letter, a lowercase letter, a number, and a special character.');
      return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setError('New password must be different from your current password.');
      return;
    }

    const user = auth.currentUser;
    if (!user?.email) {
      setError('You need to be signed in to change your password.');
      return;
    }

    setLoading(true);
    try {
      // Re-authenticate first (Firebase requires a recent login for this).
      const cred = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, next);
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (e) {
      const code = e?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Your current password is incorrect.');
      } else if (code === 'auth/weak-password') {
        setError('That new password is too weak.');
      } else if (code === 'auth/requires-recent-login') {
        setError('For security, please sign out and sign in again, then change your password.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError(e?.message || 'Could not change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key) => setShow((s) => ({ ...s, [key]: !s[key] }));

  const field = (label, value, setValue, key) => (
    <TextField
      fullWidth
      type={show[key] ? 'text' : 'password'}
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      autoComplete={key === 'current' ? 'current-password' : 'new-password'}
      sx={{ bgcolor: '#fff' }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => toggle(key)} edge="end" aria-label="toggle password visibility">
              {show[key] ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <div className="sectionContainer">
      <span className="sectionTitle">Security</span>
      <div className="profileSectionDivider" />

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 0.5, color: '#101828' }}>
          Change password
        </Typography>
        <Typography sx={{ color: '#667085', mb: 2.5 }}>
          Enter your current password, then choose a new one.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 480 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, maxWidth: 480 }}>Your password has been updated.</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
          {field('Current password', current, setCurrent, 'current')}
          {field('New password', next, setNext, 'next')}
          {field('Confirm new password', confirm, setConfirm, 'confirm')}

          <Button
            variant="contained"
            disableElevation
            disabled={loading}
            onClick={submit}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{
              alignSelf: 'flex-start', mt: 1,
              textTransform: 'none', fontWeight: 600, borderRadius: '5px',
              px: 4, py: 1.2, bgcolor: '#47b6f6', '&:hover': { bgcolor: '#1E91D6' },
            }}
          >
            Update password
          </Button>
        </Box>
      </Box>
    </div>
  );
};
